/**
 * Series → plot-local points. Renderers (Skia Path, Canvas2D lineTo) consume
 * these without re-implementing log/linear math.
 */

import {
  createLinearScale,
  createLogFreqScale,
  linearToPlot,
  logFreqToX,
  padDomain,
  type LinearScale,
  type LogFreqScale,
} from './scales';
import type { PlotFrame } from './layout';

export type PlotPoint = { x: number; y: number };

export interface XYSeries {
  /** Independent axis (Hz for FR/RT60). */
  x: number[];
  /** Dependent values (dB, seconds, …). Parallel to `x`. */
  y: number[];
}

const SPL_P_REF = 2e-5;

/** Linear pressure magnitude → dB SPL. Non-positive/zero bins become NaN
 *  (e.g. HPF-floored sub-bass) rather than -Infinity, so they never drag a
 *  viewport or a plotted line to the floor. */
export function magToDb(mag: number[]): number[] {
  return mag.map((v) => (v > 0 ? 20 * Math.log10(v / SPL_P_REF) : NaN));
}

/** Energy (power) mean of a set of dB values — the correct way to combine
 *  SPL readings, never an arithmetic mean of the dB numbers themselves.
 *  Non-finite values are ignored; null if nothing finite is left. Shared by
 *  RTA (per-band average) and FR smoothing (per-point sliding-window
 *  average) — anywhere multiple dB samples collapse into one. */
export function energyMeanDb(dbValues: number[]): number | null {
  const finite = dbValues.filter(Number.isFinite);
  if (!finite.length) return null;
  const energy = finite.reduce((sum, db) => sum + 10 ** (db / 10), 0) / finite.length;
  return 10 * Math.log10(energy);
}

/** Shift a dB curve so `refDb`'s mean lands at `targetMean` — the "honest
 *  before/after" transform: both curves shift by the SAME offset (derived
 *  from one reference), so a treatment that tames a peak still shows up as
 *  lower there, not re-floated to look like it changed nothing. */
export function shiftDbToMean(dBArr: number[], refDb: number[], targetMean = 80): number[] {
  const finite = refDb.filter(Number.isFinite);
  if (!finite.length) return dBArr;
  const mean = finite.reduce((a, b) => a + b, 0) / finite.length;
  const shift = targetMean - mean;
  return dBArr.map((v) => (Number.isFinite(v) ? v + shift : NaN));
}

export function finiteDomain(values: number[]): { lo: number; hi: number } | null {
  const finite = values.filter(Number.isFinite);
  if (!finite.length) return null;
  return { lo: Math.min(...finite), hi: Math.max(...finite) };
}

export function yDomainFromSeries(
  series: XYSeries[],
  opts?: { padFraction?: number; fallbackPad?: number; floor?: number },
): { lo: number; hi: number } {
  const all: number[] = [];
  for (const s of series) {
    for (const v of s.y) {
      if (Number.isFinite(v)) all.push(v);
    }
  }
  if (!all.length) return { lo: 0, hi: 1 };
  let { lo, hi } = padDomain(
    Math.min(...all),
    Math.max(...all),
    opts?.padFraction ?? 0.1,
    opts?.fallbackPad ?? 0.1,
  );
  if (opts?.floor != null) lo = Math.max(opts.floor, lo);
  return { lo, hi };
}

export function mapSeriesToPlot(
  series: XYSeries,
  xScale: LogFreqScale,
  yScale: LinearScale,
): PlotPoint[] {
  const out: PlotPoint[] = [];
  const n = Math.min(series.x.length, series.y.length);
  for (let i = 0; i < n; i++) {
    const f = series.x[i]!;
    const v = series.y[i]!;
    if (!Number.isFinite(f) || !Number.isFinite(v)) continue;
    if (f < xScale.fMin || f > xScale.fMax) continue;
    out.push({
      x: logFreqToX(xScale, f),
      y: linearToPlot(yScale, v),
    });
  }
  return out;
}

/** Build log-X + linear-Y scales for a frame from one or more series. */
export function scalesForLogFreqSeries(
  frame: PlotFrame,
  series: XYSeries[],
  opts?: {
    fMin?: number;
    fMax?: number;
    yPadFraction?: number;
    yFallbackPad?: number;
    yFloor?: number;
  },
): { xScale: LogFreqScale; yScale: LinearScale; yDomain: { lo: number; hi: number } } {
  const fMin = opts?.fMin ?? 20;
  const fMax = opts?.fMax ?? 200;
  const yDomain = yDomainFromSeries(series, {
    padFraction: opts?.yPadFraction,
    fallbackPad: opts?.yFallbackPad,
    floor: opts?.yFloor,
  });
  return {
    xScale: createLogFreqScale(frame.plotW, fMin, fMax),
    yScale: createLinearScale(frame.plotH, yDomain.lo, yDomain.hi, true),
    yDomain,
  };
}

/** Absolute (host) coordinates — useful when a renderer does not translate. */
export function plotPointsToView(frame: PlotFrame, pts: PlotPoint[]): PlotPoint[] {
  return pts.map((p) => ({ x: frame.plotX + p.x, y: frame.plotY + p.y }));
}

/**
 * Percentile-clipped dB viewport for a frequency-response curve (and its
 * optional baseline): windows to the 2nd/98th percentile of "signal" values
 * (above `signalFloor`) rather than the raw min/max, so a single floored bin
 * doesn't blow out the whole axis. Falls back to plain min/max ± a fixed pad
 * when there are too few signal points to take a percentile from, and to a
 * default 15 dB span around 0 when there is no finite data at all.
 */
export function dbViewportFromSeries(
  seriesValues: number[][],
  opts?: {
    signalFloor?: number;
    quantile?: [number, number];
    minPadDb?: number;
    padFraction?: number;
    roundTo?: number;
    minSpanDb?: number;
  },
): { lo: number; hi: number } {
  const signalFloor = opts?.signalFloor ?? -75;
  const [qLo, qHi] = opts?.quantile ?? [0.02, 0.98];
  const minPadDb = opts?.minPadDb ?? 2;
  const padFraction = opts?.padFraction ?? 0.1;
  const roundTo = opts?.roundTo ?? 5;
  const minSpanDb = opts?.minSpanDb ?? 15;

  const all = seriesValues.flat().filter(Number.isFinite);
  if (!all.length) return { lo: 0, hi: minSpanDb };

  const signal = all.filter((v) => v > signalFloor);
  let lo: number;
  let hi: number;
  if (signal.length >= 2) {
    const sorted = signal.slice().sort((a, b) => a - b);
    const pick = (q: number) =>
      sorted[Math.max(0, Math.min(sorted.length - 1, Math.floor(q * (sorted.length - 1))))]!;
    const pLo = pick(qLo);
    const pHi = pick(qHi);
    const pad = Math.max(minPadDb, (pHi - pLo) * padFraction);
    lo = pLo - pad;
    hi = pHi + pad;
  } else {
    lo = Math.min(...all) - 5;
    hi = Math.max(...all) + 5;
  }
  lo = Math.floor(lo / roundTo) * roundTo;
  hi = Math.ceil(hi / roundTo) * roundTo;
  if (hi - lo < minSpanDb) hi = lo + minSpanDb;
  return { lo, hi };
}

/** Auto-fit y-viewport for an absorption coefficient series (α, nominally
 *  0–1, occasionally a little above 1 with real edge/diffraction effects).
 *  Same padded-min/max shape as `dbViewportFromSeries`, minus the
 *  quantile/signal-floor machinery dB needs — α doesn't have dB's long
 *  low-end tail to trim. Never anchored at 0: a panel whose curve sits at
 *  0.6–0.8 should fill the plot, not get squashed into the top fifth of a
 *  fixed 0–1 axis. Floored at 0 only because α can't physically go
 *  negative — that's a domain fact, not a "start the axis at zero" default. */
export function alphaViewportFromSeries(
  seriesValues: number[][],
  opts?: { padFraction?: number; minPad?: number; minSpan?: number; roundTo?: number },
): { lo: number; hi: number } {
  const padFraction = opts?.padFraction ?? 0.12;
  const minPad = opts?.minPad ?? 0.03;
  const minSpan = opts?.minSpan ?? 0.1;
  const roundTo = opts?.roundTo ?? 0.05;

  const all = seriesValues.flat().filter(Number.isFinite);
  if (!all.length) return { lo: 0, hi: 1 };

  const dataLo = Math.min(...all);
  const dataHi = Math.max(...all);
  const pad = Math.max(minPad, (dataHi - dataLo) * padFraction);
  let lo = Math.max(0, dataLo - pad);
  let hi = dataHi + pad;
  lo = Math.floor(lo / roundTo) * roundTo;
  hi = Math.ceil(hi / roundTo) * roundTo;
  if (hi - lo < minSpan) hi = lo + minSpan;
  return { lo, hi };
}

/** Nearest-neighbour value at a target frequency, in log-space distance so
 *  "nearest" matches how a log-frequency axis reads perceptually. Used for
 *  crosshair/scrubber readouts: "what's the curve's value under the cursor?" */
export function nearestValueAtFreq(series: XYSeries, freq: number): number | null {
  const n = Math.min(series.x.length, series.y.length);
  let best: number | null = null;
  let bestD = Infinity;
  for (let i = 0; i < n; i++) {
    const f = series.x[i]!;
    const v = series.y[i]!;
    if (!Number.isFinite(f) || !Number.isFinite(v) || f <= 0 || freq <= 0) continue;
    const d = Math.abs(Math.log10(f) - Math.log10(freq));
    if (d < bestD) {
      bestD = d;
      best = v;
    }
  }
  return best;
}
